<template>
  <standard-dialog
    v-model="model"
    :title="restaurantTitle"
    :subtitle="$t('services_and_terms')"
    icon="mdi-handshake-outline"
    :max-width="760"
    :hide-actions="loading || !canEdit"
  >
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <v-alert v-if="!canEdit" type="info" text dense class="mb-4">
        {{ $t("services_read_only_notice") }}
      </v-alert>

      <v-card
        v-for="(service, index) in localServices"
        :key="service.service_id"
        outlined
        class="mb-3 service-card"
      >
        <v-row no-gutters align="center" class="pa-3">
          <v-checkbox
            v-model="service.is_active"
            :disabled="!canEdit"
            hide-details
            class="shrink mt-0 pt-0 mr-2"
          />
          <span class="font-weight-medium">{{ service.name }}</span>
          <v-spacer />
          <v-chip
            small
            :color="service.is_active ? 'success' : 'grey lighten-1'"
            :text-color="service.is_active ? 'white' : undefined"
          >
            {{ service.is_active ? $t("active") : $t("not_activated") }}
          </v-chip>
        </v-row>

        <template v-if="service.is_active">
          <v-divider />
          <div class="pa-4">
            <!-- Pricing block -->
            <div class="mb-4">
              <template v-if="service.pricing">
                <v-alert v-if="!service.pricing.is_verified" type="warning" text dense class="mb-2">
                  <div class="font-weight-medium">{{ $t("pricing_not_verified") }}</div>
                  <div class="caption">{{ $t("pricing_not_verified_desc") }}</div>
                </v-alert>
                <v-row no-gutters align="center">
                  <div>
                    <span class="caption font-weight-medium d-block">{{ $t("price") }}</span>
                    <span class="text-h6">
                      {{ service.pricing.opis }}
                      <v-icon v-if="!service.pricing.is_verified" small color="warning" class="ml-1">
                        mdi-alert
                      </v-icon>
                    </span>
                    <span class="caption grey--text d-block" v-if="service.pricing.valid_from">
                      {{ $t("price_valid_from") }}: {{ localeDate(service.pricing.valid_from) }}
                    </span>
                  </div>
                  <v-spacer />
                  <v-btn text small color="primary" @click="openPricing(service)">
                    <v-icon left small>mdi-history</v-icon>{{ $t("price_list") }}
                  </v-btn>
                </v-row>
              </template>
              <v-alert v-else type="warning" text dense class="mb-0">
                <v-row no-gutters align="center">
                  <div>
                    <div class="font-weight-medium">{{ $t("price_not_entered") }}</div>
                    <div class="caption">{{ $t("price_not_entered_desc") }}</div>
                  </div>
                  <v-spacer />
                  <v-btn small color="warning" depressed @click="openPricing(service)">
                    <v-icon left small>mdi-plus</v-icon>{{ $t("enter_price") }}
                  </v-btn>
                </v-row>
              </v-alert>
            </div>

            <!-- Cash flow model -->
            <div v-if="service.uses_cash_flow_model" class="mb-4">
              <span class="caption font-weight-medium d-block mb-1">
                {{ $t("cash_flow_model_label") }}
                <span class="error--text" v-if="canEdit">*</span>
              </span>
              <v-radio-group v-model="service.cash_flow_model" :readonly="!canEdit" hide-details class="mt-0">
                <v-radio v-for="option in cashFlowModelOptions" :key="option.value" :value="option.value">
                  <template v-slot:label>
                    <div>
                      <div>{{ option.text }}</div>
                      <div class="caption grey--text">{{ cashFlowModelDescription(option.value) }}</div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
              <span class="caption error--text d-block" v-if="fieldError(index, 'cash_flow_model')">
                {{ fieldError(index, "cash_flow_model") }}
              </span>
            </div>

            <!-- Payout settings -->
            <v-row>
              <v-col cols="12" sm="6">
                <dropdown
                  v-model="service.payout_schedule"
                  :items="payoutScheduleOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('payout_schedule')"
                  :readonly="!canEdit"
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="6" v-if="service.payout_schedule === 'THRESHOLD'">
                <text-field
                  v-model="service.payout_threshold_amount"
                  type="number"
                  :label="$t('payout_threshold_amount')"
                  :readonly="!canEdit"
                  required
                />
                <span class="caption error--text d-block" v-if="fieldError(index, 'payout_threshold_amount')">
                  {{ fieldError(index, "payout_threshold_amount") }}
                </span>
              </v-col>
            </v-row>
          </div>
        </template>
      </v-card>
    </template>

    <template v-slot:actions>
      <v-btn text class="mr-2" @click="close">{{ $t("cancel") }}</v-btn>
      <primary-button :loading="saving" :disabled="saving" @click="save">
        {{ $t("save_changes") }}
      </primary-button>
    </template>

    <restaurant-pricing-dialog
      v-model="pricingDialogOpen"
      :restaurant="restaurant"
      :service-id="pricingServiceId"
      :service-name="pricingServiceName"
      @priceChanged="refreshServicePricing"
    />
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import RestaurantPricingDialog from "@/components/contracts/RestaurantPricingDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import restaurantService from "@/services/restaurant";

export default {
  name: "RestaurantServicesDialog",
  mixins: [hasModel, hasSnackbar],
  components: {
    StandardDialog,
    RestaurantPricingDialog,
  },
  props: {
    restaurant: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      canEdit: true,
      restaurantMeta: null,
      localServices: [],
      options: {},
      errors: {},
      pricingDialogOpen: false,
      pricingServiceId: null,
      pricingServiceName: "",
    };
  },
  watch: {
    model(val) {
      if (val) {
        this.fetchServices();
      } else {
        this.reset();
      }
    },
  },
  computed: {
    restaurantTitle() {
      return this.restaurantMeta?.name || this.restaurant?.name || "";
    },
    payoutScheduleOptions() {
      return Object.entries(this.options.payout_schedules || {}).map(([value, text]) => ({
        value,
        text,
      }));
    },
    cashFlowModelOptions() {
      return Object.entries(this.options.cash_flow_models || {}).map(([value, text]) => ({
        value,
        text,
      }));
    },
  },
  methods: {
    async fetchServices() {
      this.loading = true;
      this.errors = {};
      try {
        const { data } = await restaurantService.getRestaurantServices(this.restaurant.id);
        this.applyData(data);
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_restaurant_services"));
        this.model = false;
      } finally {
        this.loading = false;
      }
    },
    applyData(data) {
      this.restaurantMeta = data.restaurant;
      this.canEdit = !!data.can_edit;
      this.options = data.options || {};
      this.localServices = (data.services || []).map((service) => ({
        service_id: service.service_id,
        code: service.code,
        name: service.name,
        is_active: service.is_active,
        uses_cash_flow_model: service.uses_cash_flow_model,
        pricing: service.pricing,
        payout_schedule: service.contract?.payout_schedule ?? null,
        payout_threshold_amount: service.contract?.payout_threshold_amount ?? null,
        cash_flow_model: service.contract?.cash_flow_model ?? null,
      }));
    },
    reset() {
      this.localServices = [];
      this.options = {};
      this.errors = {};
      this.canEdit = true;
      this.restaurantMeta = null;
    },
    close() {
      this.model = false;
    },
    openPricing(service) {
      this.pricingServiceId = service.service_id;
      this.pricingServiceName = service.name;
      this.pricingDialogOpen = true;
    },
    async refreshServicePricing() {
      // Only refresh the price of the one service that changed — a full
      // fetchServices()/applyData() would overwrite any unsaved edits
      // (checkboxes, payout settings) the user made to OTHER service cards
      // before opening the pricing dialog.
      try {
        const { data } = await restaurantService.getRestaurantServices(this.restaurant.id);
        const updated = (data.services || []).find((s) => s.service_id === this.pricingServiceId);
        const local = this.localServices.find((s) => s.service_id === this.pricingServiceId);
        if (updated && local) local.pricing = updated.pricing;
      } catch (error) {
        /* pricing dialog already confirmed success; ignore refresh failure */
      }
    },
    cashFlowModelDescription(value) {
      const map = {
        COURIER_PAYS_ON_PICKUP: this.$t("cash_flow_courier_pays_desc"),
        ORDERA_SETTLES_LATER: this.$t("cash_flow_ordera_settles_desc"),
      };
      return map[value] || "";
    },
    fieldError(index, field) {
      return this.errors?.[index]?.[field] || "";
    },
    validate() {
      const errors = {};
      this.localServices.forEach((service, index) => {
        if (!service.is_active) return;
        const serviceErrors = {};
        if (service.payout_schedule === "THRESHOLD" && !service.payout_threshold_amount) {
          serviceErrors.payout_threshold_amount = this.$t("validation.required");
        }
        if (service.uses_cash_flow_model && !service.cash_flow_model) {
          serviceErrors.cash_flow_model = this.$t("cash_flow_model_required");
        }
        if (Object.keys(serviceErrors).length) errors[index] = serviceErrors;
      });
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    buildPayload() {
      return this.localServices.map((service) => ({
        service_id: service.service_id,
        is_active: service.is_active,
        payout_schedule: service.payout_schedule || null,
        payout_threshold_amount:
          service.payout_threshold_amount !== "" && service.payout_threshold_amount != null
            ? Number(service.payout_threshold_amount)
            : null,
        cash_flow_model: service.uses_cash_flow_model ? service.cash_flow_model || null : null,
      }));
    },
    async save() {
      if (!this.canEdit) return;
      if (!this.validate()) {
        this.showErrorSnackbar(this.$t("please_fix_errors"));
        return;
      }
      this.saving = true;
      try {
        const { data } = await restaurantService.updateRestaurantServices(
          this.restaurant.id,
          this.buildPayload()
        );
        this.applyData(data);
        this.showSuccessSnackbar(this.$t("restaurant_services_updated_successfully"));
        this.model = false;
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.saving = false;
      }
    },
    handleSaveError(error) {
      if (error?.response?.status === 422) {
        const serverErrors = error.response.data?.errors || {};
        const parsed = {};
        Object.keys(serverErrors).forEach((key) => {
          const match = key.match(/^services\.(\d+)\.(.+)$/);
          if (!match) return;
          const index = Number(match[1]);
          const field = match[2];
          parsed[index] = parsed[index] || {};
          parsed[index][field] = serverErrors[key][0];
        });
        this.errors = parsed;
        this.showErrorSnackbar(error.response.data?.message || this.$t("please_fix_errors"));
      } else {
        this.showErrorSnackbar(this.$t("error_updating_restaurant_services"));
      }
    },
  },
};
</script>
